/// <amd-module name="Mention/IdentityAutocomplete/IdentityAutocompleteProvider" />
import * as Resources from "Mention/IdentityAutocomplete/Resources";
import { ObservableValue } from "VSS/Core/Observable";
import { PeoplePickerProvider } from "VSS/Features/Identities/IdentityPickerProvider";
import { IMarkdownTransformContext } from "VSS/Features/Markdown/MarkdownRendererContracts";
import { IMenuService } from "VSS/Features/PlatformUI/ContributedMenu";
import * as React from "react";
import * as ReactDOM from "react-dom";
import { IVssPageContext, Services, VssService } from "VSS/Platform/Context";
import { IVssLayoutManager } from "VSS/Platform/Layout";
import { IVssPageService } from "VSS/Platform/Page";
import { Button } from "VSSUI/Button";
import { getSignInAddress, IIdentity } from "VSSUI/IdentityPicker";
import { IMenuItem } from "VSSUI/Menu";
import { Observer } from "VSSUI/Observer";
import { Persona, PersonaSize } from "VSSUI/Persona";
import { Tooltip } from "VSSUI/TooltipEx";
import { css, KeyCode } from "VSSUI/Util";
import { IdentityCard } from "VSSUI/IdentityCard";
import { htmlEncode, SearchBehavior, searchString } from "Mention/Autocomplete/AutocompleteUtility";
import { IAutocompleteProvider, ISuggestionSearchResult } from "Mention/Autocomplete/Contracts";
import { isGuid } from "VSS/Core/Util/String";
import { DisplayNameStorageKeyTranslationService } from "Mention/Common/DisplayNameStorageKeyTranslationService";
import { materializeIdentity } from "Mention/Common/MaterializeIdentityProvider";
import { useStorageKeysInMentions } from "Mention/Common/Util";
import { IdentityRef } from "VSS/Platform/Contracts/WebApi";
import {
    IdentitiesGetConnectionsResponseModel as IPersonaConnections,
    IVssIdentityService,
    VssIdentityServiceName
} from "VSS/Features/Identities/IdentityService";
import { IdentityAutocompleteCommand } from "./IdentityAutocompleteCommand";
import { filterXSS } from "xss";
import { xssStyleDefaultOptions } from "xss/PresetOptions";

export interface IdentityMention {
    identity: IIdentity;
    displayName: string;
    alias: string;
}

// e.g. data-vss-mention="version:2.0,f53c4550-ba81-6f08-a728-12a6a99d1dc2"
const identityDataVssMentionPattern = /version:\d\.\d,([a-z\d\-]+)/i;

class IdentityAutocompleteProviderService extends VssService implements IAutocompleteProvider, IMenuService {
    private peopleProvider: PeoplePickerProvider;
    private displayNameTranslationService: DisplayNameStorageKeyTranslationService;
    private resolvedIdentities: { [storagekey: string]: IIdentity } = {};
    private identityPromises: { [identityId: string]: Promise<IIdentity> | undefined } = {};

    public _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);

        this.peopleProvider = new PeoplePickerProvider(pageContext);
        this.displayNameTranslationService = this.pageContext.getService<DisplayNameStorageKeyTranslationService>(
            "display-name-storage-key-translation-service"
        );
    }

    public getMenuItems(): IMenuItem[] {
        return [new IdentityAutocompleteCommand()];
    }

    public readonly shortcutCharacter = "@";

    public getSuggestions(): Promise<ISuggestionSearchResult<IdentityMention>[]> {
        return Promise.resolve(this.peopleProvider.onEmptyInputFocus()).then((identities) => this._transformIdentities(identities));
    }

    public search(term: string, isSearchEnabled: boolean): Promise<ISuggestionSearchResult<IdentityMention>[]> {
        if (term) {
            return Promise.resolve(this.peopleProvider.onFilterIdentities(term)).then((identities) => this._transformIdentities(identities, term));
        }
        return this.getSuggestions();
    }

    public renderSuggestion(item: ISuggestionSearchResult<IdentityMention>, input?: HTMLElement): JSX.Element {
        return (
            <IdentitySugestion
                item={item}
                onRequestConnectionInformation={this.onRequestConnectionInformation}
                pageContext={this.pageContext}
                input={input}
            />
        );
    }

    public getReplacementText(item: ISuggestionSearchResult<IdentityMention>): string {
        return this._getReplacementText(item.original.displayName);
    }

    private _getReplacementText(displayName: string): string {
        return `${this.shortcutCharacter}<${displayName}>`;
    }

    public getReplacementHtml(item: ISuggestionSearchResult<IdentityMention>) {
        return this._getReplacementHtml(item.original.identity);
    }

    public onKeyDown(ev: KeyboardEvent, input?: HTMLElement, selectedItem?: ISuggestionSearchResult<IdentityMention>) {
        if (ev.key === "ArrowRight" && selectedItem) {
            const layoutManager = this.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
            layoutManager.renderCallout((onDismiss) => (
                <SuggestionIdentityCard
                    identity={selectedItem.original.identity}
                    onDismiss={onDismiss}
                    onRequestConnectionInformation={this.onRequestConnectionInformation}
                    target={input}
                />
            ));
        }
    }

    private _getReplacementHtml(identity: IIdentity) {
        return `<a href="#" data-vss-mention="version:2.0,${identity.localId ?? identity.originId}">${this.shortcutCharacter}${
            identity.displayName
        }</a>`;
    }

    private _transformIdentities(identities: IIdentity[], searchTerm?: string): ISuggestionSearchResult<IdentityMention>[] {
        const results: ISuggestionSearchResult<IdentityMention>[] = [];
        identities.forEach((identity) => {
            const displayName = identity.displayName ? identity.displayName : "";
            const alias = getSignInAddress(identity);
            const original = { identity, displayName: displayName, alias };
            const highlighted = {
                identity,
                displayName: this._highlightText(displayName, searchTerm),
                alias: this._highlightText(alias, searchTerm)
            };
            results.push({ original, highlighted: highlighted });
        });
        return results;
    }

    private _highlightText(input: string, searchTerm?: string): string {
        if (!searchTerm) {
            return input;
        }

        let highlightSubstitutionPattern = `<span class="font-weight-heavy">$&</span>`; // TODO: common constant
        let terms = searchTerm
            .split(/\s+/)
            .filter((term) => !!term)
            .map((term) => htmlEncode(term));

        // Identity service does not filter on domain, so only string match on end of input
        const domainIndex = input.lastIndexOf("\\");
        const searchResult = searchString(input.substr(domainIndex + 1), terms, SearchBehavior.Prefix, highlightSubstitutionPattern);
        let allTermsFound: boolean = true;
        terms.forEach((term) => {
            if (!searchResult.found[term]) {
                allTermsFound = false;
            }
        });
        if (allTermsFound) {
            const prefix = domainIndex > 0 ? input.substr(0, domainIndex + 1) : "";
            return prefix + searchResult.highlightedSource;
        }
        return input;
    }

    /**
     * Given an Identity mention, this will fetch the identity and then render the mention
     * so that text content like '@<displayName>' will become a nice looking link.
     */
    public transformMarkdown(transformContext: IMarkdownTransformContext, ignoreMatch?: (matchIndex: number) => boolean): void {
        transformContext.markup = transformContext.markup.replace(/\@\<(.*?)\>/g, (fullMatch: string, identityId: string, offset: number) => {
            if (ignoreMatch && ignoreMatch(offset)) {
                return fullMatch;
            }

            const identityMention = new ObservableValue<IIdentity | null>(null);

            if (useStorageKeysInMentions(this.pageContext) && isGuid(identityId)) {
                // the mention is @<storageKey>
                const identity = this.peopleProvider.getEntityFromUniqueAttribute(identityId);
                const identityPromise = identity as Promise<IIdentity>;
                const updateIdentity = (resolvedIdentity: IIdentity) => {
                    identityMention.value = resolvedIdentity;
                    this.displayNameTranslationService.addDisplayNameAndStorageKey(
                        this.displayNameTranslationService.getIdentityId(resolvedIdentity),
                        identityId
                    );
                };
                if (identityPromise.then) {
                    identityPromise.then((resolvedIdentity) => {
                        updateIdentity(resolvedIdentity);
                    });
                } else {
                    updateIdentity(identity as IIdentity);
                }
            } else {
                // the mention is @<displayName>
                let uniqueAttribute: string | null = identityId;
                if (useStorageKeysInMentions(this.pageContext)) {
                    uniqueAttribute = this.displayNameTranslationService.getStorageKeyUsingDisplayName(identityId);
                }
                if (uniqueAttribute) {
                    const identity = this.resolvedIdentities[uniqueAttribute] || this.peopleProvider.getEntityFromUniqueAttribute(uniqueAttribute);
                    const identityPromise = identity as unknown as Promise<IIdentity>;
                    if (identityPromise.then) {
                        identityPromise.then((resolvedIdentity: IIdentity) => {
                            this.resolvedIdentities[uniqueAttribute!] = resolvedIdentity;
                            identityMention.value = resolvedIdentity;
                        });
                    } else {
                        identityMention.value = identity as IIdentity;
                    }
                }
            }

            return transformContext.createReplacement(
                <Observer identityMention={identityMention}>
                    {({ identityMention }: { identityMention: IIdentity | null }) => {
                        if (identityMention) {
                            return (
                                <IdentityMention
                                    displayNameTranslationService={this.displayNameTranslationService}
                                    identity={identityMention}
                                    onRequestConnectionInformation={this.onRequestConnectionInformation}
                                    pageContext={this.pageContext}
                                />
                            );
                        } else {
                            return fullMatch;
                        }
                    }}
                </Observer>,
                "span"
            );
        });
    }

    /**
     * Given an html document containing mentions as raw anchor tags, this function will replace those mentions
     * with nice-looing identity links.
     */
    public async transformHtml(element: HTMLElement): Promise<void> {
        await Promise.all(
            Array.from(element.querySelectorAll("a[data-vss-mention]")).map(async (a: HTMLAnchorElement) => {
                const match = identityDataVssMentionPattern.exec(a.getAttribute("data-vss-mention") ?? "");
                if (!match) {
                    return;
                }

                const identityId = isGuid(match[1]) ? match[1] : undefined;
                if (!identityId) {
                    return;
                }

                let identityPromise: Promise<IIdentity>;
                if (this.identityPromises[identityId]) {
                    identityPromise = this.identityPromises[identityId]!;
                } else {
                    identityPromise = Promise.resolve(this.peopleProvider.getEntityFromUniqueAttribute(identityId));
                    this.identityPromises[identityId] = identityPromise;
                }

                const identity = await identityPromise;
                if (!identity) {
                    return;
                }

                this.displayNameTranslationService.addDisplayNameAndStorageKey(
                    this.displayNameTranslationService.getIdentityId(identity),
                    identityId
                );

                const replacement = (
                    <IdentityMention
                        displayNameTranslationService={this.displayNameTranslationService}
                        identity={identity}
                        onRequestConnectionInformation={this.onRequestConnectionInformation}
                        pageContext={this.pageContext}
                    />
                );

                const newRoot = document.createElement("span");
                a.replaceWith(newRoot);
                ReactDOM.render(replacement, newRoot);
            })
        );
    }

    private onRequestConnectionInformation = (
        entity: IIdentity,
        getDirectReports?: boolean | undefined
    ): IPersonaConnections | PromiseLike<IPersonaConnections> => {
        if (!entity) {
            return {} as IPersonaConnections;
        }
        const identityService = this.pageContext.getService<IVssIdentityService>(VssIdentityServiceName);

        return identityService.getConnections(entity, getDirectReports);
    };

    public onSelect(item: ISuggestionSearchResult<IdentityMention>, isHtml?: boolean): ObservableValue<string | null> {
        const mangledDisplayText = new ObservableValue<string | null>(null);
        if (!item.original.identity.localId) {
            materializeIdentity(item.original.identity, this.pageContext).then((storageKey: string) => {
                const identity = this.peopleProvider.getEntityFromUniqueAttribute(storageKey);
                const identityPromise = identity as Promise<IIdentity>;
                if (identityPromise.then) {
                    identityPromise.then((pickerIdentity: IIdentity) => {
                        const displayText = this.displayNameTranslationService.getIdentityId(pickerIdentity);
                        mangledDisplayText.value = isHtml
                            ? this._getReplacementHtml(pickerIdentity)
                            : this._getReplacementText(this.displayNameTranslationService.addDisplayNameAndStorageKey(displayText, storageKey));
                        this.peopleProvider.addIdentitiesToMRU([pickerIdentity]);
                    });
                }
            });
        } else {
            mangledDisplayText.value = isHtml
                ? this._getReplacementHtml(item.original.identity)
                : this._getReplacementText(
                      this.displayNameTranslationService.addDisplayNameAndStorageKey(
                          this.displayNameTranslationService.getIdentityId(item.original.identity),
                          item.original.identity.localId
                      )
                  );
            this.peopleProvider.addIdentitiesToMRU([item.original.identity]);
        }
        return mangledDisplayText;
    }
}

interface IIdentityMentionProps {
    displayNameTranslationService: DisplayNameStorageKeyTranslationService;
    identity: IIdentity;
    onRequestConnectionInformation: (
        entity: IIdentity,
        getDirectReports?: boolean | undefined
    ) => IPersonaConnections | PromiseLike<IPersonaConnections>;
    pageContext: IVssPageContext;
}

interface IIdentitySugestionProps {
    input?: HTMLElement;
    item: ISuggestionSearchResult<IdentityMention>;
    onRequestConnectionInformation: (
        entity: IIdentity,
        getDirectReports?: boolean | undefined
    ) => IPersonaConnections | PromiseLike<IPersonaConnections>;
    pageContext: IVssPageContext;
}

function IdentitySugestion(props: IIdentitySugestionProps) {
    const { original, highlighted } = props.item;

    const title = highlighted ? highlighted.displayName : original.displayName;
    const subtitle = highlighted ? highlighted.alias : original.alias;

    const titleHtml = {
        __html: filterXSS(title, xssStyleDefaultOptions)
    };

    const aliasHtml = {
        __html: filterXSS(subtitle, xssStyleDefaultOptions)
    };

    return (
        <div className="flex-row">
            <Persona className="identity-icon" identity={original.identity} size={PersonaSize.size24} />
            <div className="flex-column flex-start">
                <Tooltip text={original.displayName}>
                    <span className="identity-title" dangerouslySetInnerHTML={titleHtml} />
                </Tooltip>
                <Tooltip text={original.alias}>
                    <span className="identity-subtitle" dangerouslySetInnerHTML={aliasHtml} />
                </Tooltip>
            </div>
            <Button
                ariaLabel={Resources.IdentityPickerProfileCardButtonTooltip}
                iconProps={{ iconName: "ContactCard", className: "flex-shrink" }}
                className="bolt-contact-card-button identity-suggestion-contact-card-button flex-noshrink"
                subtle={true}
                onClick={(event) => {
                    const layoutManager = props.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
                    layoutManager.renderCallout((onDismiss) => (
                        <SuggestionIdentityCard
                            identity={original.identity}
                            onDismiss={onDismiss}
                            onRequestConnectionInformation={props.onRequestConnectionInformation}
                            target={props.input}
                        />
                    ));
                }}
            ></Button>
        </div>
    );
}

function IdentityMention(props: IIdentityMentionProps) {
    const { displayNameTranslationService, identity, onRequestConnectionInformation, pageContext } = props;
    const identityToolip = displayNameTranslationService.getIdentityId(identity);
    const identityRef = React.createRef<HTMLSpanElement>();
    const showCardObservable = new ObservableValue<boolean>(false);
    return (
        <Observer showCard={showCardObservable}>
            {({ showCard }: { showCard: boolean }) => (
                <>
                    <span
                        ref={identityRef}
                        role="button"
                        tabIndex={0}
                        className={css(
                            "mention-preview-person font-weight-heavy cursor-pointer",
                            isCurrentUser(identity, pageContext) && "mention-preview-me"
                        )}
                        data-rendered-mention="people"
                        onClick={() => (showCardObservable.value = !showCard)}
                        onKeyDown={(event: React.KeyboardEvent<HTMLElement>) => {
                            if (event.which === KeyCode.enter || event.which === KeyCode.space) {
                                showCardObservable.value = !showCard;
                            }
                        }}
                        title={identityToolip !== identity.displayName ? identityToolip : undefined}
                    >
                        {`@${identity.displayName}`}
                    </span>
                    {showCard && (
                        <IdentityCard
                            getEntityFromUniqueAttribute={() => identity}
                            key={identity.entityId}
                            identity={identity}
                            displayName={identity.displayName}
                            target={identityRef.current ? identityRef.current : undefined}
                            onDismissCallback={() => (showCardObservable.value = false)}
                            onRequestConnectionInformation={onRequestConnectionInformation}
                        />
                    )}
                </>
            )}
        </Observer>
    );
}

interface ISuggestionIdentityCardProps {
    identity: IIdentity;
    onDismiss: () => void;
    onRequestConnectionInformation: (
        entity: IIdentity,
        getDirectReports?: boolean | undefined
    ) => IPersonaConnections | PromiseLike<IPersonaConnections>;
    target?: HTMLElement;
}

function SuggestionIdentityCard(props: ISuggestionIdentityCardProps) {
    return (
        <IdentityCard
            getEntityFromUniqueAttribute={() => props.identity}
            key={props.identity.entityId}
            identity={props.identity}
            displayName={props.identity.displayName}
            onDismissCallback={props.onDismiss}
            onRequestConnectionInformation={props.onRequestConnectionInformation}
            target={props.target}
        />
    );
}

function isCurrentUser(identity: IIdentity, pageContext: IVssPageContext): boolean {
    const pageService = pageContext.getService<IVssPageService>("IVssPageService");
    const pageData = pageService.getData();
    let currentUser: IdentityRef | undefined;
    if (pageData) {
        currentUser = pageData.user;
    }
    if (!currentUser || !identity.localId) {
        return false;
    }

    return currentUser.id.toUpperCase() === identity.localId.toUpperCase();
}

Services.add("identity-autocomplete-service", { serviceFactory: IdentityAutocompleteProviderService });
